/**
 * Decide which status tabs stay visible and which move into the "More" menu,
 * from measured widths only (no DOM access, so it is unit-testable).
 */

export interface ComputeVisibleTabsInput {
  /** Natural width of each tab, in stage order. */
  widths: number[];
  /** Width of the "More" trigger. */
  moreWidth: number;
  /** Horizontal gap between adjacent items (tabs and the "More" trigger). */
  gap: number;
  /** Width available for tabs plus the "More" trigger. */
  available: number;
  /** Index of the active tab, or -1 when none is active. */
  activeIndex: number;
}

export interface VisibleTabsResult {
  /** Indices rendered as tabs, in stage order. */
  visibleIndices: number[];
  /** Indices rendered in the "More" menu, in stage order. */
  overflowIndices: number[];
}

const range = (count: number): number[] =>
  Array.from({ length: count }, (_, index) => index);

/** Longest prefix of `widths` whose tabs (plus `reserved` px) fit in `budget`. */
const countFittingPrefix = (
  widths: number[],
  gap: number,
  budget: number,
  reserved = 0
): number => {
  let used = reserved;
  let count = 0;
  for (const width of widths) {
    const next = used + width + (count > 0 || reserved > 0 ? gap : 0);
    if (next > budget) break;
    used = next;
    count += 1;
  }
  return count;
};

/**
 * Fit tabs in stage order. When not everything fits, reserve room for "More"
 * and, if the active tab would overflow, swap it into the last visible slot so
 * it always stays directly visible. At least one tab is always visible; the
 * renderer truncates it when even that does not fit.
 */
export function computeVisibleTabs({
  widths,
  moreWidth,
  gap,
  available,
  activeIndex,
}: ComputeVisibleTabsInput): VisibleTabsResult {
  const total = widths.length;
  if (total === 0) return { visibleIndices: [], overflowIndices: [] };

  if (countFittingPrefix(widths, gap, available) === total) {
    return { visibleIndices: range(total), overflowIndices: [] };
  }

  const hasActive = activeIndex >= 0 && activeIndex < total;
  const budget = available - moreWidth - gap;
  const prefixCount = countFittingPrefix(widths, gap, budget);

  let visibleIndices: number[];
  if (!hasActive || activeIndex < prefixCount) {
    visibleIndices = range(Math.max(prefixCount, 1));
  } else {
    const beforeActive = countFittingPrefix(
      widths.slice(0, activeIndex),
      gap,
      budget,
      widths[activeIndex]
    );
    visibleIndices = [...range(beforeActive), activeIndex];
  }

  const visibleSet = new Set(visibleIndices);
  return {
    visibleIndices,
    overflowIndices: range(total).filter((index) => !visibleSet.has(index)),
  };
}

/** Raw DOM measurements of the tab row, in CSS pixels (may be fractional). */
export interface TabOverflowMetrics {
  /** Width of the observed container, set by its parent. */
  containerWidth: number;
  /** Natural width of each tab button, in stage order. */
  tabWidths: number[];
  /** Gap between items inside the track (tabs and the "More" trigger). */
  tabGap: number;
  /** Left + right padding and border of the track. */
  trackPaddingX: number;
  /** Natural width of the "More" trigger (sits inside the track). */
  moreWidth: number;
  /** Gap between the track and the trailing element in the row. */
  rowGap: number;
  /** Width of the trailing element (e.g. create stage), 0 when absent. */
  trailingWidth: number;
}

/**
 * Map row measurements onto `computeVisibleTabs`. Item widths round up and the
 * container rounds down so sub-pixel error never lets a visible tab clip.
 */
export function fitMeasuredTabs(
  metrics: TabOverflowMetrics,
  activeIndex: number
): VisibleTabsResult {
  const trailing =
    metrics.trailingWidth > 0 ? Math.ceil(metrics.trailingWidth) + metrics.rowGap : 0;
  return computeVisibleTabs({
    widths: metrics.tabWidths.map(Math.ceil),
    moreWidth: Math.ceil(metrics.moreWidth),
    gap: metrics.tabGap,
    available:
      Math.floor(metrics.containerWidth) - Math.ceil(metrics.trackPaddingX) - trailing,
    activeIndex,
  });
}
